import { Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import { DogTitlesApi, DogTitleSearch } from "../../../../api";
import { AddButton, InformationTooltip } from "../../../../components";
import { useEventsContext, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalDataTable } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { DogTitlesDrupalPanelStack } from "./DogTitlesDrupalPanelStack";
import { DrupalDogForeignTitleGrid } from "./DrupalDogForeignTitleGrid";

export interface IDrupalDogTitleListProps {
  dogId: number;
  refresh: () => void;
}

export const DrupalDogTitleList: React.FunctionComponent<IDrupalDogTitleListProps> = ({ dogId, refresh }) => {
  const api = useApiService(DogTitlesApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const { dispatchEvent } = useEventsContext();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 10, 15],
    pageSize: 10,
    sortKeys: { createdOn: "DESC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogTitleSearch) => {
      return api.baseSearch(DogTitleSearch.fromJS({ ...sObj, dogId: dogId, languageId: currentLanguage.languageId }));
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "titleName",
      },
      {
        header: () => t(ETLCodes.Country),
        fieldName: "country",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "titleType",
      },
      {
        header: () => t(ETLCodes.Year),
        fieldName: "year",
      },
      {
        header: () => t(ETLCodes.CreationDate),
        fieldName: "createdOn",
      },
    ],
    [t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <DrupalFieldSet
        title={
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "1rem" }}>{t(ETLCodes.Titles)}</div>{" "}
            <div>
              <InformationTooltip customMessage={t(ETLCodes.TitleNotDisplayedBeforeValidation)} />
            </div>{" "}
          </div>
        }
        rightElement={
          <AddButton
            onClick={(e) => {
              e.stopPropagation();
              addItemFunction();
            }}
            text={t(ETLCodes.General_Add)}
            intent={Intent.PRIMARY}
            secured={false}
          />
        }
      >
        <DrupalDataTable tableState={tableState} loading={loading} columns={columns} keyFieldName="dogTitleId" />
      </DrupalFieldSet>
      <DrupalFieldSet
        title={
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "1rem" }}>{t(ETLCodes.CurrentTitleRequest)}</div>
          </div>
        }
      >
        <DrupalDogForeignTitleGrid dogId={dogId} />
      </DrupalFieldSet>
      <Dialog
        title={t(ETLCodes.TitleSelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1300, height: 500, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <DogTitlesDrupalPanelStack
            dogId={dogId}
            onSaved={() => {
              setDialogOpened(false);
              search();
              dispatchEvent("RefreshSearchTables");
              refresh();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
